import { Button } from "@coval/ui/button"

import {
  ClerkActionProvider,
  useClerkActions,
} from "@/components/auth/ClerkActionProvider"

import { useOrganizationUserManagement } from "../hooks/useOrganizationUserManagement"
import { UserManagementPanel } from "./UserManagementPanel"

function PageControls({
  page,
  total,
  hasNext,
  busy,
  previous,
  next,
}: {
  page: number
  total: number
  hasNext: boolean
  busy: boolean
  previous: () => void
  next: () => void
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-coval-text-muted">
      <span>
        {total} {total === 1 ? "result" : "results"} · Page {page}
      </span>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          disabled={busy || page === 1}
          onClick={previous}
        >
          Previous
        </Button>
        <Button variant="secondary" disabled={busy || !hasNext} onClick={next}>
          Next
        </Button>
      </div>
    </div>
  )
}

function ReadState({
  error,
  retry,
  loading,
}: {
  error: Error | null
  retry: () => void
  loading: boolean
}) {
  if (error) {
    return (
      <div
        role="alert"
        className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-coval-danger-border bg-coval-danger-surface p-3 text-sm text-coval-danger-on-surface"
      >
        <span>{error.message}</span>
        <Button variant="secondary" onClick={retry}>
          Retry
        </Button>
      </div>
    )
  }
  if (loading) {
    return (
      <p role="status" className="mb-4 text-sm text-coval-text-muted">
        Loading…
      </p>
    )
  }
  return null
}

export function OrganizationUserManagement() {
  return (
    <ClerkActionProvider>
      <OrganizationUserManagementContent />
    </ClerkActionProvider>
  )
}
function OrganizationUserManagementContent() {
  const { reverify } = useClerkActions()
  const state = useOrganizationUserManagement(reverify)
  const members = state.members.data
  const invitations = state.invitations.data
  return (
    <UserManagementPanel
      data={{
        members: members ?? [],
        invitations: invitations ?? [],
      }}
      currentUserId={state.currentUserId}
      canManage={state.canManage}
      actions={state.actions}
      memberSearch={state.search}
      onMemberSearchChange={state.onSearchChange}
      adminCount={state.adminCount}
      memberCount={state.members.total}
      invitationCount={state.invitations.total}
      refreshing={
        state.members.isFetching ||
        state.invitations.isFetching ||
        !state.mutationsReady ||
        !!state.members.error
      }
      membersState={
        <ReadState
          error={state.members.error}
          loading={
            state.members.total === undefined && state.members.isFetching
          }
          retry={() => {
            void state.members.refetch()
          }}
        />
      }
      invitationsState={
        <ReadState
          error={state.invitations.error}
          loading={
            state.invitations.total === undefined &&
            state.invitations.isFetching
          }
          retry={() => {
            void state.invitations.refetch()
          }}
        />
      }
      membersPagination={
        members && (
          <PageControls
            page={state.membersPage}
            total={state.members.total ?? 0}
            hasNext={state.members.hasNextPage}
            busy={state.members.isFetching}
            previous={state.membersPrevious}
            next={state.membersNext}
          />
        )
      }
      invitationsPagination={
        invitations && (
          <PageControls
            page={state.invitationsPage}
            total={state.invitations.total ?? 0}
            hasNext={state.invitations.hasNextPage}
            busy={state.invitations.isFetching}
            previous={state.invitationsPrevious}
            next={state.invitationsNext}
          />
        )
      }
    />
  )
}
